/**
 * share-import — web bridge to the native iOS share extension.
 *
 * The DugoutImport share extension copies GameChanger files (scorebook PDF, game
 * stats PDF, stats CSV) into an App Group inbox and tries to open
 * `dugout://import`. This module drains that inbox via the ShareImportPlugin and
 * hands back real File objects for the one import screen (GameDataImport), which
 * identifies each by content.
 *
 * No-ops on web (the plugin only exists on native).
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

interface SharedFile { name: string; data: string } // data = base64
interface ShareImportPluginDef {
  getSharedFiles(): Promise<{ files: SharedFile[] }>;
  clearShareLog(): Promise<void>;
}

const ShareImportPlugin = registerPlugin<ShareImportPluginDef>('ShareImportPlugin');

/** Decode a base64 string into a Uint8Array (browser-safe, no Buffer). */
function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * Drain any files the coach shared into Dugout. Returns [] on web or when the
 * inbox is empty. The native side deletes the files as it returns them, so this
 * is safe to call on every app open/resume without re-importing.
 *
 * Both GameChanger PDFs and the stats CSV come through here, so the MIME type is
 * derived from the filename rather than hardcoded: the import router sends a
 * CSV to the CSV parser and everything else to the PDF loader, and a CSV labelled
 * application/pdf would be handed to pdfjs and throw.
 */
export async function drainSharedFiles(): Promise<File[]> {
  if (!Capacitor.isNativePlatform()) return [];
  try {
    const { files } = await ShareImportPlugin.getSharedFiles();
    return (files || []).map(f => {
      const bytes = base64ToBytes(f.data);
      // The extension prefixes "<ms>-<random>-" to keep staged names unique;
      // the coach should see GameChanger's own filename.
      const name = (f.name || 'shared.pdf').replace(/^\d+-\d+-/, '');
      const type = /\.pdf$/i.test(name) ? 'application/pdf' : /\.csv$/i.test(name) ? 'text/csv' : 'text/plain';
      return new File([bytes], name, { type });
    });
  } catch {
    return [];
  }
}

/** Reset the share extension's running checklist once a game is saved, so the
 *  next game's first share starts from an empty list. No-op on web. */
export async function clearShareLog(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try { await ShareImportPlugin.clearShareLog(); } catch { /* older native build */ }
}
